"use client";

import { SafeHydrate } from "@/SafeHydrate";
import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Dialog, DialogContent, DialogTrigger, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { MapPin, Calendar, ZoomIn, Camera, Music, Users } from "lucide-react";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const galleryItems = [{
  id: 1,
  category: "Klassiskt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Villa-Arvika.webp?v=4ajpf27h47v",
  title: "Villa Arvika",
  date: "2023-11-15",
  location: "Arvika, Värmland",
  description: "En tidlös badrumsdröm i carraramarmor med platsbyggd förvaring och mässingsdetaljer."
}, {
  id: 2,
  category: "Klassiskt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Sekelskifte-Karlstad.webp?v=bzhlpoldv3w",
  title: "Sekelskifte Karlstad",
  date: "2023-12-02",
  location: "Karlstad Centrum",
  description: "Varsam renovering av ett sekelskiftesbadrum med klassiskt kakel och fristående badkar."
}, {
  id: 3,
  category: "Modernt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Industriell-Loft.webp?v=5hpj8n6xs4i",
  title: "Industriell Loft",
  date: "2024-01-10",
  location: "Hammarö",
  description: "Minimalistisk design med stora klinkerplattor i betong-look och svarta detaljer."
}, {
  id: 4,
  category: "Lyx",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/lyxigt-duschbadrum1.webp?v=0pi0yetaair",
  title: "Spa-oas Sunne",
  date: "2024-02-14",
  location: "Selma Spa-området",
  description: "Exklusivt hemmaspa med integrerad bastu, dubbla takduschar och stämningsbelysning."
}, {
  id: 5,
  category: "Klassiskt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Herrgardens.badrum.webp?v=21dztsdlc1q",
  title: "Herrgårdens Badrum",
  date: "2023-10-30",
  location: "Säffle",
  description: "Påkostad renovering i en historisk miljö med fokus på symmetri och elegans."
}, {
  id: 6,
  category: "Modernt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Nordisk-minimalism.webp?v=w2hcpjuzhz",
  title: "Nordisk Minimalism",
  date: "2024-01-12",
  location: "Karlstad",
  description: "Ljust och luftigt badrum med detaljer i ljust trä och dolda rördragningar."
}, {
  id: 7,
  category: "Lyx",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Rod.matta_.webp?v=1lwp8jur545",
  title: "Penthouse Karlstad",
  date: "2023-09-20",
  location: "Tullholmen",
  description: "Toppmodern badrumsdesign med panoramautsikt och exklusiva materialval."
}, {
  id: 8,
  category: "Klassiskt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Traditionell.Charm_.webp?v=vs9q1e111rl",
  title: "Traditionell Charm",
  date: "2023-11-15",
  location: "Charlottenberg",
  description: "Ett charmigt badrum som kombinerar modern funktion med gammaldags estetik."
}, {
  id: 9,
  category: "Modernt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/modernt-badrum-inbyggda-hyllor.webp?v=pjx5y8hqlmp",
  title: "Grå Harmoni",
  date: "2024-01-20",
  location: "Arvika",
  description: "Ton-i-ton design med inbyggda nischer och modern belysningsstyrning."
}, {
  id: 10,
  category: "Lyx",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Lyxigt.badrum2.webp?v=or0hyh8cjor",
  title: "Marmor & Guld",
  date: "2024-03-01",
  location: "Forshaga",
  description: "Ett badrum som andas lyx med stora marmorytor och guldpläterade blandare."
}, {
  id: 11,
  category: "Klassiskt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Lantligt-badrum.webp?v=dv35898eh6f",
  title: "Lantligt Badrum",
  date: "2023-12-02",
  location: "Kil",
  description: "Rustik elegans med pärlspont och klassiskt porslin."
}, {
  id: 12,
  category: "Modernt",
  src: "https://proffsbadrum.se/wp-content/uploads/2026/09/Stram-design.webp?v=x8vygexvcji",
  title: "Stram Design",
  date: "2024-01-11",
  location: "Hammarö",
  description: "Fokus på rena linjer och funktionell förvaring i ett mindre badrum."
}];
const categories = ["Klassiskt", "Modernt", "Lyx"];
export default function FullstandigtGalleri() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState("Klassiskt");
  const [openDialogId, setOpenDialogId] = useState<number | null>(null);
  const [imageUrl, setImageUrl] = useState("");
  return <section data-section-id="59443" id="inspirerande-showroom" ref={ref} className={`bg-background text-foreground py-20 px-4 md:px-8 min-h-screen transition-opacity duration-1000 ${isVisible ? "opacity-100" : "opacity-0"}`}>
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 space-y-4">
          <h2 className="text-4xl md:text-5xl font-default font-semibold tracking-tight bg-gradient-to-r from-primary via-blue-400 to-primary bg-clip-text text-transparent">Showroom för badrumsrenovering i Värmland</h2>
          <p className="text-muted-foreground max-w-2xl mx-auto text-lg font-default">Upptäck inspiration för exklusiva badrum med olika material, färger och stilar. Här visar vi ett urval av badrumsprojekt, från klassiska detaljer till moderna och lyxiga badrumslösningar.</p>
        </div>

        <Tabs defaultValue="Klassiskt" className="w-full flex flex-col items-center" onValueChange={setActiveTab}>
          <TabsList className="mb-10 bg-secondary border border-border p-1 rounded-md backdrop-blur-sm">
            {categories.map((category, i) => <TabsTrigger key={category} value={category} data-index={i} className="rounded-md px-6 py-2 text-sm md:text-base font-default data-[state=active]:bg-primary data-[state=active]:text-primary-foreground transition-all duration-300">
                {category === "Klassiskt" && <Music className="w-4 h-4 mr-2" />}
                {category === "Modernt" && <Camera className="w-4 h-4 mr-2" />}
                {category === "Lyx" && <Users className="w-4 h-4 mr-2" />}
                {category}
              </TabsTrigger>)}
          </TabsList>

          {categories.map((category, i) => <TabsContent key={category} value={category} data-index={i} className="w-full mt-0 focus-visible:outline-none focus-visible:ring-0">
              <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
                {galleryItems.filter(item => item.category === category).map((item, index) => <Dialog key={item.id} open={openDialogId === item.id} onOpenChange={open => {
              if (open) {
                setImageUrl(item.src);
                setOpenDialogId(item.id);
              } else {
                setOpenDialogId(null);
              }
            }}>
                      <DialogTrigger asChild>
                        <Button type="button" variant="ghost" data-index={index} className="group relative aspect-[3/4] overflow-hidden rounded-lg cursor-pointer border border-border bg-muted/10 shadow-sm hover:shadow-md hover:border-primary/50 transition-all duration-500 text-left p-0 h-auto w-full">
                          <img src={item.src} alt={item.title} className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-110" />
                          <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex flex-col justify-end p-6">
                            <div className="transform translate-y-4 group-hover:translate-y-0 transition-transform duration-300">
                              <h3 className="text-white font-default font-semibold text-lg mb-1">
                                {item.title}
                              </h3>
                              <p className="text-primary-foreground text-sm font-medium flex items-center gap-2">
                                <ZoomIn className="w-3 h-3" /> Se detaljer
                              </p>
                            </div>
                          </div>
                        </Button>
                      </DialogTrigger>
                      <DialogContent className="max-w-2xl md:max-w-4xl w-[95vw] p-6 md:p-8 overflow-hidden bg-background border-border backdrop-blur-xl sm:rounded-lg">
                        <div className="grid grid-cols-1 lg:grid-cols-3 h-full max-h-[85vh] lg:max-h-[80vh]">
                          
                          <div className="lg:col-span-2 relative bg-black flex items-center justify-center overflow-hidden h-[50vh] lg:h-auto">
                            <img src={item.src} alt={item.title} className="w-full h-full object-contain lg:object-cover" />
                          </div>

                          
                          <div className="lg:col-span-1 p-6 lg:p-8 flex flex-col justify-center bg-card/50 backdrop-blur-md border-t lg:border-t-0 lg:border-l border-border relative w-full">
                            <DialogTitle className="text-2xl md:text-3xl font-default font-semibold text-foreground mb-2 break-words w-full">
                              {item.title}
                            </DialogTitle>
                            <DialogDescription className="text-muted-foreground mb-6 text-base font-default break-words w-full">
                              {item.description}
                            </DialogDescription>

                            <div className="space-y-4 mb-8">
                              <div className="flex items-center gap-3 text-sm text-foreground/80 font-default">
                                <div className="p-2 rounded-md bg-primary/10 text-primary">
                                  <SafeHydrate><Calendar className="w-4 h-4" /></SafeHydrate>
                                </div>
                                <span>Färdigställt: {item.date}</span>
                              </div>
                              <div className="flex items-center gap-3 text-sm text-foreground/80 font-default">
                                <div className="p-2 rounded-md bg-primary/10 text-primary">
                                  <MapPin className="w-4 h-4" />
                                </div>
                                <span>Plats: {item.location}</span>
                              </div>
                            </div>

                            <div className="mt-auto pt-6 border-t border-border pb-6">
                              <div className="flex flex-col sm:flex-row gap-4 overflow-visible w-full md:w-auto">
                                <div className="w-full">
                                  <Button onClick={() => {
                            setOpenDialogId(null);
                            navigate("/offertforfragan");
                          }} variant="default" className="w-full md:w-auto px-6 py-3 bg-primary hover:bg-primary/90 text-primary-foreground font-default">
                                    Begär offert
                                  </Button>
                                </div>
                                <Link to={imageUrl} download className="w-full md:w-auto">
                                  <Button variant="outline" className="w-full md:w-auto px-6 py-3 border-border hover:bg-muted text-foreground font-default">
                                    Visa i fullskärm
                                  </Button>
                                </Link>
                              </div>
                            </div>
                          </div>
                        </div>
                      </DialogContent>
                    </Dialog>)}
              </div>
            </TabsContent>)}
        </Tabs>

        <div className="mt-16 text-center">
          <Link to="/QuoteRequestPage">
            <Button size="lg" variant="outline" className="border-primary text-primary hover:bg-primary hover:text-white transition-all duration-300 px-8 py-6 text-lg rounded-md font-default shadow-sm hover:shadow-md">
              {activeTab === "Klassiskt" && "Få en kostnadsfri offert för ditt klassiskt badrum"}
              {activeTab === "Modernt" && "Få en kostnadsfri offert för ditt moderna badrum"}
              {activeTab === "Lyx" && "Få en kostnadsfri offert för ditt lyxiga badrum"}
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}